import { faCalendarDay, faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { Trans } from '@lingui/react/macro';
import { YesNoDialog } from '@/components/dialogs';
import { Dropdown, DropdownContextMenu } from '@ygtechnologies/rubus-ui';
import { DateDisplay } from '@/components/localization';
import { isEqual, orderBy } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { deleteProjectDeadline, saveProjectDeadline } from '@/services/deadlines';
import {
    getComplexProjectDeadlines,
    getProjectDeadlinesWithReferenceCount,
    GetResidentialUnitsDeadlinesForProjectDeadline,
    ProjectDeadlineUpdateInfo,
    revertAllDeadlines,
} from '@/services/projects';
import { ProjectDeadline, ProjectDeadlineWithReferenceCount } from '@/types';
import { useAsyncState } from '@/utils/hooks';
import ProjectSettingsComplexDeadlines from '../ProjectSettingsComplexDeadlines';
import SettingsSection from '../SettingsSection';
import DeadlineForm from './DeadlineForm';
import theme from './ProjectSettingsDeadlines.module.scss';
import { Toast } from '@ygtechnologies/bygr-frontend-component-library';

type ProjectSettingsDeadlinesProps = {
    projectId: number;
};

const ProjectSettingsDeadlines: React.FC<ProjectSettingsDeadlinesProps> = ({ projectId }) => {
    const [deadlines, setDeadlines, reloadDeadlines] = useAsyncState(
        () => getProjectDeadlinesWithReferenceCount(projectId),
        [projectId],
        []
    );

    const [activeDeadline, setActiveDeadline] = useState<ProjectDeadline | null>(null);
    const [deferredDeadlineClicked, setDeferredDeadlineClicked] = useState<ProjectDeadline | null>(null);
    const [complexDeadlineEditItem, setComplexDeadlineEditItem] = useState<ProjectDeadline | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const newDraftIdRef = useRef(0);

    const [rugDeadlines, , reloadRugDeadlines] = useAsyncState(() => getComplexProjectDeadlines(projectId), [projectId], []);
    const [ruDeadlines] = useAsyncState(
        () =>
            activeDeadline && activeDeadline.id > 0
                ? GetResidentialUnitsDeadlinesForProjectDeadline(activeDeadline.id)
                : Promise.resolve(null),
        [activeDeadline?.id],
        null
    );

    const deadlineHasGroupEdits = rugDeadlines?.some((x) => x.edited) ?? false;
    const deadlineHasUnitEdits =
        activeDeadline && activeDeadline.id > 0 ? (ruDeadlines?.some((x) => x.id === activeDeadline.id) ?? false) : false;

    // a defained active deadline with a negative id means itsn a draft.
    const isDraft = activeDeadline !== null && activeDeadline.id < 0;

    useEffect(() => {
        if (activeDeadline == null && deadlines.length > 0) {
            setActiveDeadline(deadlines[0]);
        }
    }, [deadlines, activeDeadline]);

    const onEditDeadlineClose = () => {
        setComplexDeadlineEditItem(null);
        reloadRugDeadlines();
    };
    const handleComplexDeadlineEditItem = (deadline: ProjectDeadline) => {
        // Don't allow complex editing for draft deadlines
        if (deadline.id < 0) {
            return;
        }
        setComplexDeadlineEditItem(deadline);
        setActiveDeadline(deadline);
    };

    const handleNewClick = () => {
        if (isDraft) return;

        const newDraft: ProjectDeadline = {
            id: --newDraftIdRef.current,
            projectId: projectId,
            name: '',
            description: '',
            deadline: new Date(),
            isChangeRequestDeadline: false,
        };
        setActiveDeadline(newDraft);
    };

    const handleDeleteClick = async (deadline: ProjectDeadlineWithReferenceCount) => {
        if (isDeadlineInUse(deadline)) {
            return;
        }
        await deleteProjectDeadline(deadline.id);
        await reloadDeadlines();

        if (activeDeadline && activeDeadline.id === deadline.id) {
            setActiveDeadline(null);
        }
        reloadRugDeadlines();
    };

    const isDeadlineInUse = (deadline: ProjectDeadlineWithReferenceCount) => {
        return deadline.referenceCount > 0 || deadline.isChangeRequestDeadline;
    };

    const hasChanges = (): boolean => {
        if (!activeDeadline) return false;

        if (isDraft) {
            return (
                activeDeadline.name.trim() !== '' ||
                activeDeadline.description.trim() !== '' ||
                activeDeadline.isChangeRequestDeadline !== false
            );
        }

        const unmodified = deadlines.find((x) => x.id === activeDeadline.id);
        if (!unmodified) return false;

        const normalize = (dl: ProjectDeadline) => ({
            name: dl.name,
            description: dl.description,
            deadline: dl.deadline instanceof Date ? dl.deadline.getTime() : new Date(dl.deadline).getTime(),
            isChangeRequestDeadline: dl.isChangeRequestDeadline,
        });
        return !isEqual(normalize(activeDeadline), normalize(unmodified));
    };

    const doRevertAll = async () => {
        const deadline = activeDeadline;
        if (!deadline || deadline.id < 0) {
            Toast.error(<Trans>An error has occured</Trans>);
            return;
        }
        setIsSaving(true);

        try {
            await revertAllDeadlines(activeDeadline.id);
            await reloadDeadlines();
            setActiveDeadline(activeDeadline);
        } finally {
            setIsSaving(false);
            Toast.success(<Trans>All deadlines has been reset to project default</Trans>);
            reloadRugDeadlines();
        }
    };

    const doSave = async () => {
        const deadline = activeDeadline;

        if (!deadline) {
            return;
        }
        setIsSaving(true);

        const data: ProjectDeadlineUpdateInfo = {
            id: isDraft ? null : deadline.id,
            projectId,
            name: deadline.name,
            description: deadline.description,
            deadline: deadline.deadline,
            isChangeRequestDeadline: deadline.isChangeRequestDeadline,
        };

        try {
            const savedDeadline = await saveProjectDeadline(data);
            const updatedList = await getProjectDeadlinesWithReferenceCount(projectId);
            setDeadlines(updatedList);
            const fromList = updatedList.find((d) => d.id === savedDeadline.id) ?? savedDeadline;
            setActiveDeadline(fromList);
            Toast.success(<Trans>Saved deadlines</Trans>);
        } catch (error) {
            Toast.error(<Trans>Failed to save deadline</Trans>);
            throw error;
        } finally {
            setIsSaving(false);
            reloadRugDeadlines();
        }
    };

    const selectDeadline = (deadline: ProjectDeadline) => {
        if (isDraft) {
            Toast.warn(<Trans>You have unsaved changes. Please save or discard them first.</Trans>);
            return;
        }
        if (hasChanges()) {
            Toast.warn(<Trans>You have unsaved changes. Please save or discard them first.</Trans>);
            setDeferredDeadlineClicked({ ...deadline });
        } else {
            setActiveDeadline({ ...deadline });
        }
    };

    const cancelDraft = () => {
        setActiveDeadline(deadlines[0] || null);
    };

    return (
        <SettingsSection>
            <SettingsSection.List>
                {orderBy(deadlines, ['name']).map((dl) => (
                    <SettingsSection.ListItem
                        key={dl.id}
                        active={activeDeadline?.id === dl.id}
                        onClick={(e) => selectDeadline(dl)}>
                        <span>{dl.name}</span>
                        <small>
                            <DateDisplay value={dl.deadline} />
                        </small>
                        {dl.isChangeRequestDeadline && (
                            <small>
                                <Trans>Change request deadline</Trans>
                            </small>
                        )}

                        <div className={theme.itemDropdown}>
                            <DropdownContextMenu>
                                <Dropdown.Item onClick={() => handleComplexDeadlineEditItem(dl)} icon={faCalendarDay}>
                                    <Trans>Edit deadlines by floor or unit group</Trans>
                                </Dropdown.Item>
                                <Dropdown.Confirm
                                    dialogTitle={<Trans>Confirm delete</Trans>}
                                    dialogBody={<Trans>Are you sure you want to delete "{dl.name}"?</Trans>}
                                    disabled={isDeadlineInUse(dl)}
                                    onConfirm={() => {
                                        handleDeleteClick(dl);
                                    }}
                                    icon={faTrashAlt}>
                                    <Trans>Delete</Trans>
                                </Dropdown.Confirm>
                            </DropdownContextMenu>
                        </div>
                    </SettingsSection.ListItem>
                ))}

                <SettingsSection.ListItemAddButton
                    onClick={isDraft ? undefined : handleNewClick}
                    active={false}
                    disabled={isDraft}>
                    {!isDraft && <Trans>Add new deadline</Trans>}
                    {isDraft && (
                        <small className={theme.disabledButtonText}>
                            <Trans>Save current deadline</Trans>
                        </small>
                    )}
                </SettingsSection.ListItemAddButton>
            </SettingsSection.List>
            <SettingsSection.Content>
                {activeDeadline && (
                    <>
                        {isDraft && (
                            <div className={theme.draftBanner}>
                                <Trans>Draft Deadline - This deadline has not been saved yet</Trans>
                            </div>
                        )}
                        <DeadlineForm
                            value={activeDeadline}
                            hasGroupEdits={deadlineHasGroupEdits}
                            hasUnitEdits={deadlineHasUnitEdits}
                            onChange={setActiveDeadline}
                            onSaveClick={doSave}
                            onRevertClick={doRevertAll}
                            onCancelClick={isDraft ? cancelDraft : undefined}
                            isSaving={isSaving}
                        />
                    </>
                )}

                <ProjectSettingsComplexDeadlines
                    onClose={onEditDeadlineClose}
                    onAccept={onEditDeadlineClose}
                    show={!!complexDeadlineEditItem}
                    projectId={projectId}
                    projectDeadlineId={complexDeadlineEditItem?.id ?? null}
                />
            </SettingsSection.Content>

            <YesNoDialog
                show={!!deferredDeadlineClicked}
                title={<Trans>Discard changes</Trans>}
                body={<Trans>Are you sure you want to continue without saving?</Trans>}
                onAccept={() => {
                    if (deferredDeadlineClicked) setActiveDeadline(deferredDeadlineClicked);
                    setDeferredDeadlineClicked(null);
                }}
                onClose={() => setDeferredDeadlineClicked(null)}
            />
        </SettingsSection>
    );
};

export default ProjectSettingsDeadlines;
